import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  type DataGridProps,
  type GridColDef,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Alert, Box, Button, Paper, Tooltip } from '@mui/material';
import { useMemo } from 'react';

interface DataTableProps<T extends { id: number | string }> {
  rows: T[];
  columns: GridColDef[];
  loading?: boolean;
  rowCount?: number;
  paginationMode?: 'server' | 'client';
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (m: GridPaginationModel) => void;
  errorMessage?: string;
  emptyMessage?: string;
  /**
   * Explicit fixed height. When omitted the table fills its flex parent
   * (recommended) — host pages should wrap it in a flex column so the
   * grid stretches to the available viewport height.
   */
  height?: number | string;
  /**
   * Hide the toolbar (column filters, quick search, density, columns,
   * export). Default is to show it so users can search/filter every list.
   */
  disableToolbar?: boolean;
  /** Hide just the Filters button from the toolbar. */
  hideFilters?: boolean;
  /** Hide just the (placeholder) Import button from the toolbar. */
  hideImport?: boolean;
  /**
   * Hide the quick-search box. Use in `filterMode="server"` tables, where a
   * global free-text search has no backend equivalent and would silently do
   * nothing.
   */
  hideQuickFilter?: boolean;
  /**
   * Sorting mode. `'server'` lets the caller re-query on sort change via
   * `sortModel` + `onSortModelChange`; defaults to client-side (loaded rows).
   */
  sortingMode?: 'server' | 'client';
  sortModel?: GridSortModel;
  onSortModelChange?: (m: GridSortModel) => void;
  /**
   * Filtering mode. `'server'` lets the caller re-query on filter change via
   * `filterModel` + `onFilterModelChange`; defaults to client-side.
   */
  filterMode?: 'server' | 'client';
  filterModel?: GridFilterModel;
  onFilterModelChange?: (m: GridFilterModel) => void;
  /**
   * Double-click a row to open a detail panel. When set, rows also get a
   * pointer cursor so the affordance is discoverable.
   */
  onRowDoubleClick?: DataGridProps['onRowDoubleClick'];
}

/**
 * Toolbar shown above every list table. Reproduces the buttons MUI's
 * `<GridToolbar>` ships (Columns / Filters / Density / Export) plus a
 * disabled IMPORT button — placeholder for the bulk-upload feature that
 * isn't built yet. Quick-search sits at the right edge, debounced so each
 * keystroke doesn't refilter immediately.
 *
 * Lives next to the DataGrid (not exported) so every page that uses
 * `<DataTable>` gets the new button for free.
 */
function DataTableToolbar({
  hideFilters,
  hideImport,
  hideQuickFilter,
}: {
  hideFilters?: boolean;
  hideImport?: boolean;
  hideQuickFilter?: boolean;
}) {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      {!hideFilters && <GridToolbarFilterButton />}
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      {!hideImport && (
        <Tooltip title="Import — coming soon">
          {/* `span` wrapper is needed because Tooltip can't render a tooltip
              on a disabled button (pointer events are off on the button). */}
          <span>
            <Button
              size="small"
              startIcon={<FileUploadOutlinedIcon />}
              disabled
              aria-label="import (disabled)"
            >
              Import
            </Button>
          </span>
        </Tooltip>
      )}
      <Box sx={{ flex: 1 }} />
      {!hideQuickFilter && <GridToolbarQuickFilter debounceMs={250} />}
    </GridToolbarContainer>
  );
}

/**
 * Standard table for resource list views. Wraps `<DataGrid>` with a paper
 * container, error/empty states, the toolbar above, and a tuned default
 * height so callers don't have to repeat boilerplate per page.
 *
 * Filtering and the quick-search box operate on the **currently loaded
 * rows** — since most list pages use server-side pagination, that's the
 * current page only. Acceptable for the typical small admin datasets.
 */
export function DataTable<T extends { id: number | string }>({
  rows,
  columns,
  loading,
  rowCount,
  paginationMode = 'server',
  paginationModel,
  onPaginationModelChange,
  errorMessage,
  emptyMessage = 'No records found.',
  height,
  disableToolbar,
  hideFilters,
  hideImport,
  hideQuickFilter,
  sortingMode,
  sortModel,
  onSortModelChange,
  filterMode,
  filterModel,
  onFilterModelChange,
  onRowDoubleClick,
}: DataTableProps<T>) {
  // Bind the toolbar's button flags into a stable component so the grid
  // doesn't remount the toolbar on every render (which would close any
  // open columns/density popover).
  const Toolbar = useMemo(
    () => () => (
      <DataTableToolbar
        hideFilters={hideFilters}
        hideImport={hideImport}
        hideQuickFilter={hideQuickFilter}
      />
    ),
    [hideFilters, hideImport, hideQuickFilter],
  );
  // When the caller doesn't pin a height, the paper + the inner grid box
  // claim the remaining vertical space of the parent flex column so the
  // table never bottoms out at a stubby 540 px on tall viewports.
  const flexFill = height === undefined;
  return (
    <Paper
      sx={{
        width: '100%',
        ...(flexFill && {
          flex: 1,
          minHeight: 320,
          display: 'flex',
          flexDirection: 'column',
        }),
      }}
    >
      {errorMessage && (
        <Alert severity="error" sx={{ m: 2 }}>{errorMessage}</Alert>
      )}
      <Box
        sx={
          flexFill
            ? { flex: 1, minHeight: 0, width: '100%' }
            : { height, width: '100%' }
        }
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationMode={paginationMode}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          sortingMode={sortingMode}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          filterMode={filterMode}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          onRowDoubleClick={onRowDoubleClick}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          // Use `initialState` (not the `density` prop) — the latter is a
          // controlled value and would override the toolbar's density toggle
          // so users couldn't switch compact/comfortable.
          initialState={{ density: 'standard' }}
          slots={{
            toolbar: disableToolbar ? undefined : Toolbar,
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                {loading ? 'Loading…' : emptyMessage}
              </Box>
            ),
          }}
          sx={{
            border: 0,
            ...(onRowDoubleClick && { '& .MuiDataGrid-row': { cursor: 'pointer' } }),
          }}
        />
      </Box>
    </Paper>
  );
}
